/**
 * Canonical pricing constants for the UI.
 *
 * These are now DERIVED from the Pricing Engine™ (`src/pricing/`) — this file
 * holds no numbers and no arithmetic of its own. It exists so the many existing
 * imports keep working while the engine owns the truth.
 *
 * New code should prefer `calculatePrice()` from `@/pricing`.
 */

import {
  CAMPAIGNS,
  COUPONS,
  DEFAULT_REGION,
  REGIONS,
  calculatePrice,
  formatMoney,
  PRICING_VERSION,
} from "@/pricing";

const IN = REGIONS[DEFAULT_REGION];
const US = REGIONS.US;

/** Pricing Policy version behind every number on this page. */
export const PRICING_POLICY_VERSION = PRICING_VERSION;

/** INR list price (MRP), struck through wherever the offer is shown. */
export const INR_LIST_PRICE = IN.listPrice;

const LAUNCH = CAMPAIGNS.find((c) => c.id === "INDEPENDENCE_2026")!;

/**
 * The headline offer the whole app talks about.
 *
 * The 80% flash offer takes over every pill, strip,
 * countdown and checkout line while its three-day window is open. Once it
 * closes, the app falls back to the standing Launch Offer price with
 * no code change and no deploy.
 */
const FLASH = COUPONS.find((c) => c.id === "FESTIVAL80")!;

const flashOpen = (() => {
  const now = Date.now();
  return (
    FLASH.active !== false &&
    now >= new Date(FLASH.validFrom!).getTime() &&
    now <= new Date(FLASH.validUntil!).getTime()
  );
})();

/** INR price reached with the headline code. Computed by the engine. */
export const INR_OFFER_PRICE = flashOpen
  ? calculatePrice({ couponCode: FLASH.id, now: new Date(FLASH.validFrom!) }).finalAmount
  : INR_LIST_PRICE; // no live offer: the real price is the list price

/** What the learner saves against the list price. */
export const INR_SAVINGS = INR_LIST_PRICE - INR_OFFER_PRICE;

/**
 * International price in USD, charged via PayPal.
 *
 * NOTE: $499 is the canonical international list price (MRP), not an FX
 * conversion of the INR price. The engine models it as the `US` region's own MRP
 * and never converts. Coupons discount off this number in USD.
 */
export const USD_LIST_PRICE = US.listPrice;

/** USD price reached with the headline code, in USD, never FX-converted. */
export const USD_OFFER_PRICE = flashOpen
  ? calculatePrice({
      region: "US",
      couponCode: FLASH.id,
      now: new Date(FLASH.validFrom!),
    }).finalAmount
  : US.listPrice;

/** Offer end — one constant, used by every pill, strip, countdown and terms line. */
export const OFFER_ENDS = new Date(flashOpen ? FLASH.validUntil! : LAUNCH.validUntil!);

/** Coupon code that unlocks the headline price. */
export const OFFER_COUPON_CODE = flashOpen ? FLASH.id : "";

export const OFFER_NAME = flashOpen ? "Festival Offer — 80% OFF" : "";

/** True while the extended 80% flash offer is live. */
export const OFFER_IS_FLASH = flashOpen;


export function formatInr(amount: number): string {
  return formatMoney(amount, "INR");
}

export function formatUsd(amount: number): string {
  return formatMoney(amount, "USD");
}

/** "31 August 2026" — the human form of OFFER_ENDS. */
export const OFFER_ENDS_LABEL = OFFER_ENDS.toLocaleDateString("en-IN", {
  day: "numeric",
  month: "long",
  year: "numeric",
  timeZone: "Asia/Kolkata",
});

export const OFFER_ENDS_LABEL_LONG = `${OFFER_ENDS_LABEL}, 23:59 IST`;

export const INR_LIST_PRICE_LABEL = formatInr(INR_LIST_PRICE);
export const INR_OFFER_PRICE_LABEL = formatInr(INR_OFFER_PRICE);
export const INR_SAVINGS_LABEL = formatInr(INR_SAVINGS);
export const USD_OFFER_PRICE_LABEL = formatUsd(USD_OFFER_PRICE);
export const USD_LIST_PRICE_LABEL = formatUsd(USD_LIST_PRICE);
/** "Save 79%" style headline for the flash offer, empty when it is closed. */
export const OFFER_DISCOUNT_LABEL = OFFER_IS_FLASH ? "80% off" : `Save ${INR_SAVINGS_LABEL}`;

/** "August 20" — the day the headline offer closes, in IST. */
export const OFFER_ENDS_DAY_LABEL = OFFER_ENDS.toLocaleDateString("en-US", {
  month: "long",
  day: "numeric",
  timeZone: "Asia/Kolkata",
});

/**
 * The full headline promise. One sentence, used verbatim by the sitewide FOMO
 * bar, the hero, the pricing section and checkout so the same words follow the
 * learner all the way through.
 */
export const OFFER_HEADLINE = OFFER_IS_FLASH
  ? `Festival Offer — 80% off until ${OFFER_ENDS_DAY_LABEL}`
  : `Launch Offer — ${INR_OFFER_PRICE_LABEL}, ends ${OFFER_ENDS_LABEL}.`;

/**
 * What "Freedom Offer" means, in plain, confidence-building words. Shown next to
 * the headline so the discount never feels like a trick.
 */
export const OFFER_EXPLAINER_TITLE = "What the Festival Offer means";
export const OFFER_EXPLAINER_LINES = OFFER_IS_FLASH
  ? [
      `Our launch promise: the full 12-month WeKIT™ Career Clarity 360™ journey at ${USD_OFFER_PRICE_LABEL} / ${INR_OFFER_PRICE_LABEL} instead of ${USD_LIST_PRICE_LABEL} / ${INR_LIST_PRICE_LABEL}.`,
      `Code ${OFFER_COUPON_CODE} is applied for you at checkout — 80% comes straight off the list price, nothing is added later.`,
      `One payment. No renewals, no hidden fees, and every future upgrade in your 12 months is included.`,
    ]
  : [];

/** Short urgency lines reused across the home page. */
export const OFFER_FOMO_LINES = OFFER_IS_FLASH
  ? [
      `Festival Offer — 80% off until ${OFFER_ENDS_DAY_LABEL}.`,
      `After ${OFFER_ENDS_DAY_LABEL} the price returns to ${USD_LIST_PRICE_LABEL} / ${INR_LIST_PRICE_LABEL}.`,
      "One simple price for the full 12-month journey.",
    ]
  : [];



/** Both currencies side by side, e.g. "$100 / ₹1,000" — for shared, non-checkout surfaces. */
export const DUAL_LIST_PRICE_LABEL = `${USD_LIST_PRICE_LABEL} / ${INR_LIST_PRICE_LABEL}`;
export const DUAL_OFFER_PRICE_LABEL = `${USD_OFFER_PRICE_LABEL} / ${INR_OFFER_PRICE_LABEL}`;
